import React, { useState } from "react";

import { Alert, Button, RatesDownloads, Loader } from "@partials";
import { usePagination } from "@hooks";
import { AlertType } from "@constants";
import { getLocalization } from "@utilities";
import ExchangeChart from "./components/ExchangeChart";

import { ExchangeRatesDetailProps, LocalizationTexts, TableRowItem } from "./types";

// Props - Shared (mobile, dekstop)
type TableProps = {
  items: TableRowItem[];
  hasNextItems: boolean;
  isLoading: boolean;
  loadMoreItems: () => void;
  localization: LocalizationTexts;
};

// Props - TableForMobile
type MobileRowProps = {
  localization: LocalizationTexts;
  text: keyof LocalizationTexts;
  value: string;
};

// Props - TableForDesktop
type DesktopHeaderWhiteProps = {
  title: string;
  subTitle1: string;
  subTitle2: string;
  marginRight?: boolean;
};

type DesktopRateWrapperProps = {
  index: number;
  value1: string;
  value2: string;
  marginRight?: boolean;
};

// TableForMobile
const MobileRow: React.FC<MobileRowProps> = ({ localization, text, value }) => {
  return (
    <div className="ex-rates-table-mobile__row">
      <span>{getLocalization(localization, text)}</span>
      <span>{value}</span>
    </div>
  );
};

const TableForMobile: React.FC<TableProps> = ({
  items,
  hasNextItems,
  isLoading,
  loadMoreItems,
  localization,
}) => {
  return (
    <div className="ex-rates-table-mobile">
      {items.map((item) => {
        return (
          <div
            key={`mobile-key-${item.id}${item.curencyIso}${item.time}`}
            className="ex-rates-table-mobile__box"
          >
            <p className="ex-rates-table-mobile__title">
              <span>{getLocalization(localization, "History_Date")}</span>
              <span>{item.date}</span>
            </p>
            <MobileRow localization={localization} text="History_Time" value={item.time} />
            <MobileRow
              localization={localization}
              text="Mobile_History_KBMiddle"
              value={item.middleKBStr}
            />
            <MobileRow
              localization={localization}
              text="Mobile_History_CNBMiddle"
              value={item.middleCNBStr}
            />
            <MobileRow
              localization={localization}
              text="Mobile_History_CashBuy"
              value={item.cashBuyStr}
            />
            <MobileRow
              localization={localization}
              text="Mobile_History_CashSell"
              value={item.cashSellStr}
            />
            <MobileRow
              localization={localization}
              text="Mobile_History_NoCashBuy"
              value={item.nonCashBuyStr}
            />
            <MobileRow
              localization={localization}
              text="Mobile_History_NoCashSell"
              value={item.nonCashSellStr}
            />
          </div>
        );
      })}

      {hasNextItems && (
        <div className="mt-8">
          <Button
            template="secondary"
            title={getLocalization(localization, "LoadMore")}
            type="button"
            isLoading={isLoading}
            onClick={loadMoreItems}
            dataTestId="button-load-more-mobile"
          />
        </div>
      )}
    </div>
  );
};

// TableForDesktop
const DesktopHeaderWhite: React.FC<DesktopHeaderWhiteProps> = ({
  title,
  subTitle1,
  subTitle2,
  marginRight,
}) => {
  return (
    <div
      className={`
        ex-rates-table-desktop__col
        ex-rates-table-desktop__value
        ex-rates-table-desktop__header__wrapper
        ${marginRight ? "mr-8" : "mr-0"}
      `}
    >
      <div className="ex-rates-table-desktop__header__title">{title}</div>
      <div className="flex items-center justify-between">
        <span
          className="
            ex-rates-table-desktop__header__subtitle
            ex-rates-table-desktop__value-inner
            ex-rates-table-desktop__value-inner--header"
        >
          {subTitle1}
        </span>
        <span
          className="
            ex-rates-table-desktop__header__subtitle
            ex-rates-table-desktop__value-inner
            ex-rates-table-desktop__value-inner--header"
        >
          {subTitle2}
        </span>
      </div>
    </div>
  );
};

const DesktopRateWrapper: React.FC<DesktopRateWrapperProps> = ({
  index,
  value1,
  value2,
  marginRight,
}) => {
  return (
    <div
      className={`
        ex-rates-table-desktop__col
        ex-rates-table-desktop__value
        ex-rates-table-desktop__rate-wrapper
        ${marginRight ? "mr-8" : "mr-0"}
      `}
    >
      <span
        className={`
          ex-rates-table-desktop__value-inner
          ex-rates-table-desktop__value-inner-${index}
          ex-rates-table-desktop__rate-value
        `}
      >
        {value1}
      </span>
      <span
        className={`
          ex-rates-table-desktop__value-inner
          ex-rates-table-desktop__value-inner-${index}
          ex-rates-table-desktop__rate-value
        `}
      >
        {value2}
      </span>
    </div>
  );
};

const TableForDesktop: React.FC<TableProps> = ({
  items,
  hasNextItems,
  isLoading,
  loadMoreItems,
  localization,
}) => {
  return (
    <div className="ex-rates-table-desktop">
      {/* table - header */}
      <div className="ex-rates-table-desktop__header">
        <div
          className="
            ex-rates-table-desktop__col
            ex-rates-table-desktop__name
            ex-rates-table-desktop__name--header
            mr-8"
        >
          <div className="ex-rates-table-desktop__header__date-time">
            <span
              className="
                ex-rates-table-desktop__header__subtitle
                ex-rates-table-desktop__value-inner
                ex-rates-table-desktop__value-inner--header"
            >
              {getLocalization(localization, "History_Date")}
            </span>
            <span
              className="
                ex-rates-table-desktop__header__subtitle
                ex-rates-table-desktop__value-inner
                ex-rates-table-desktop__value-inner--header"
            >
              {getLocalization(localization, "History_Time")}
            </span>
          </div>
        </div>
        <DesktopHeaderWhite
          title={getLocalization(localization, "Desktop_History_Middle")}
          subTitle1={getLocalization(localization, "Desktop_History_KB")}
          subTitle2={getLocalization(localization, "Desktop_History_CNB")}
          marginRight
        />
        <DesktopHeaderWhite
          title={getLocalization(localization, "Desktop_History_ExchangeCash")}
          subTitle1={getLocalization(localization, "Desktop_History_Buy")}
          subTitle2={getLocalization(localization, "Desktop_History_Sell")}
          marginRight
        />
        <DesktopHeaderWhite
          title={getLocalization(localization, "Desktop_History_ExchangeWire")}
          subTitle1={getLocalization(localization, "Desktop_History_Buy")}
          subTitle2={getLocalization(localization, "Desktop_History_Sell")}
        />
      </div>

      {/* table - 1 item = row */}
      {items.map((item, index) => {
        return (
          <div
            key={`table-key-${item.id}${item.curencyIso}${item.time}`}
            className="ex-rates-table-desktop__item"
          >
            <div
              className={`
                ex-rates-table-desktop__col
                ex-rates-table-desktop__name
                ex-rates-table-desktop__name-${index}
                ex-rates-table-desktop__name-date-time-wrapper
              `}
            >
              <span className="ex-rates-table-desktop__name-date-time-value">{item.date}</span>
              <span className="ex-rates-table-desktop__name-date-time-value">{item.time}</span>
            </div>
            <DesktopRateWrapper
              index={index}
              value1={item.middleKBStr}
              value2={item.middleCNBStr}
              marginRight
            />
            <DesktopRateWrapper
              index={index}
              value1={item.cashBuyStr}
              value2={item.cashSellStr}
              marginRight
            />
            <DesktopRateWrapper
              index={index}
              value1={item.nonCashBuyStr}
              value2={item.nonCashSellStr}
            />
          </div>
        );
      })}
      {/* table header - footer */}
      <div className="ex-rates-table-desktop__footer">
        <div
          aria-hidden="true"
          className="
            ex-rates-table-desktop__col
            ex-rates-table-desktop__name
            ex-rates-table-desktop__name--footer
            mr-8"
        />
        <div
          aria-hidden="true"
          className="
            ex-rates-table-desktop__col
            ex-rates-table-desktop__value
            ex-rates-table-desktop__footer__wrapper
            mr-8"
        />
        <div
          aria-hidden="true"
          className="
            ex-rates-table-desktop__col
            ex-rates-table-desktop__value
            ex-rates-table-desktop__footer__wrapper
            mr-8"
        />
        <div
          aria-hidden="true"
          className="
            ex-rates-table-desktop__col
            ex-rates-table-desktop__value
            ex-rates-table-desktop__footer__wrapper"
        />
      </div>

      {hasNextItems && (
        <div className="mt-12">
          <Button
            template="secondary"
            title={getLocalization(localization, "LoadMore")}
            type="button"
            isLoading={isLoading}
            onClick={loadMoreItems}
            dataTestId="button-load-more-desktop"
          />
        </div>
      )}
    </div>
  );
};

const ExchangeRatesDetail: React.FC<ExchangeRatesDetailProps> = ({
  api,
  query,
  items: defaultItems,
  pagination: defaultPagination,
  localization,
  cultureCode,
  baseUrl,
  currencyIso,
  downloads,
  iconsList,
  showChart,
}) => {
  // usePagination hook
  const {
    hasNextItems,
    isLoadingMore,
    items,
    hasError,
    isLoading,
    loadMoreItemsExchangeRatesDetail,
  } = usePagination({
    api,
    defaultQuery: query,
    defaultItems,
    defaultPagination,
    cultureCode,
    fetchBodyData: { baseUrl },
    currency: currencyIso,
    date: query,
  });

  const [chartData, setChartData] = useState({
    fromDate: query,
    toDate: query,
    timeFrame: "1",
  });

  const handleDataChange = (data) => {
    setChartData(data);
  };

  return (
    <>
      {showChart && (
        <ExchangeChart
          onDataChange={handleDataChange}
          localization={localization}
          currencyIso={currencyIso}
          cultureCode={cultureCode}
          queryDate={query}
          iconsList={iconsList}
        />
      )}
      <div>
        {!showChart && (
          <div className="mb-6 text-2xl">
            {getLocalization(localization, "History_Title")}{" "}
            <span className="uppercase">{currencyIso}</span>
          </div>
        )}
        {hasError && (
          <div className="mb-12">
            <Alert
              type={AlertType.Error}
              text={getLocalization(localization, "TableError")}
              textAttributes={{ role: "alert", "aria-live": "assertive" }}
            />
          </div>
        )}
        {isLoading ? (
          <div className="mb-12 flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <div>
            <TableForMobile
              items={items}
              hasNextItems={hasNextItems}
              isLoading={isLoadingMore}
              loadMoreItems={loadMoreItemsExchangeRatesDetail}
              localization={localization}
            />
            <TableForDesktop
              items={items}
              hasNextItems={hasNextItems}
              isLoading={isLoadingMore}
              loadMoreItems={loadMoreItemsExchangeRatesDetail}
              localization={localization}
            />
          </div>
        )}
      </div>
      <RatesDownloads
        isVisible={downloads.isVisible}
        title={downloads.title}
        links={downloads.links}
        iconsList={iconsList}
        apiUrl={downloads.apiUrl}
        cultureCode={cultureCode}
        fromDate={chartData.fromDate}
        toDate={chartData.toDate}
        timeFrame={chartData.timeFrame}
        currency={currencyIso}
      />
    </>
  );
};

export default ExchangeRatesDetail;
